import { MagnifyingGlassIcon } from '@phosphor-icons/react';
import { useMemo, useRef, useState } from 'react';

import { Checkbox } from '@/components/common/checkbox';
import { Select } from '@/components/common/select';
import { type CursorPage, useCursorSelect } from '@/hooks/use-cursor-select';

import type { PickerValue } from '../../types';

import styles from './async-multi-select.module.scss';

export const ALL_VALUE = '__ALL__';
// The meta endpoints' `@Max(100)` page size — the fewest round trips to load the full list.
const ALL_PAGE_LIMIT = 100;

// Module-level label cache, one Map per `cacheKey` (device/bundle/customer), never cleared for the
// tab's lifetime. `useCursorSelect`'s options only ever cover the currently-loaded page(s) for the
// *current* search term, so a previously-picked id can fall outside them after the wizard step
// remounts (React unmounts Step 1's fields between steps) or after the admin clears a search term
// that had surfaced it — either way antd is then handed a selected value with no matching option
// and falls back to rendering the raw id (117114). Caching every label ever seen, independent of
// component lifecycle or query key, is what lets the picker always redraw the real name.
const labelCaches = new Map<string, Map<string, string>>();
function getLabelCache(cacheKey: string) {
  let cache = labelCaches.get(cacheKey);
  if (!cache) {
    cache = new Map();
    labelCaches.set(cacheKey, cache);
  }
  return cache;
}

export type AsyncMultiSelectProps<TRaw, TRef> = {
  value: PickerValue<TRef>;
  onChange: (value: PickerValue<TRef>) => void;
  allLabel: string;
  /** Groups this picker's label cache (117114) — pass a stable value per picker kind, e.g. 'device'. */
  cacheKey: string;
  queryKey: (search: string) => readonly unknown[];
  fetchPage: (p: { search: string; cursor?: string; limit: number }) => Promise<CursorPage<TRaw>>;
  toOption: (raw: TRaw) => { value: string; label: string };
  toItem: (raw: TRaw) => TRef;
  itemId: (ref: TRef) => string;
  placeholder?: string;
  status?: 'error';
  id?: string;
  'aria-label'?: string;
};

export function AsyncMultiSelect<TRaw, TRef>({
  value,
  onChange,
  allLabel,
  cacheKey,
  queryKey,
  fetchPage,
  toOption,
  toItem,
  itemId,
  ...rest
}: AsyncMultiSelectProps<TRaw, TRef>) {
  // Every picker here feeds a save: re-read on each form open so a stale entity is never offered.
  const cursor = useCursorSelect({ queryKey, fetchPage, toOption, refetchOnMount: 'always' });

  // A newly-picked value's raw item is always in the currently-loaded pages; an already-picked
  // ref is already in `value.ids`. Rebuild refs from those two — no mutable accumulation needed.
  const refByValue = useMemo(() => {
    const map = new Map<string, TRef>(value.ids.map((r) => [itemId(r), r]));
    for (const page of cursor.query.data?.pages ?? [])
      for (const raw of page.data) map.set(toOption(raw).value, toItem(raw));
    return map;
  }, [cursor.query.data, value.ids, toOption, toItem, itemId]);

  const selected = value.all ? [ALL_VALUE] : value.ids.map(itemId);
  const selectedSet = new Set(selected);

  const options = useMemo(() => {
    const cache = getLabelCache(cacheKey);
    for (const o of cursor.options) cache.set(o.value, o.label);
    const known = new Set(cursor.options.map((o) => o.value));
    const remembered = value.ids
      .map(itemId)
      .filter((id) => !known.has(id))
      .map((id) => ({ value: id, label: cache.get(id) ?? id }));
    return [{ value: ALL_VALUE, label: allLabel }, ...cursor.options, ...remembered];
  }, [cacheKey, cursor.options, value.ids, itemId, allLabel]);

  // The complete, unsearched list — needed to expand "All" into individual refs when one row is
  // unchecked, and to tell when re-picking the last missing row should collapse back to "All"
  // (AC 109605, 121006). Loaded page-by-page on demand and kept for the component's lifetime.
  const allItemsRef = useRef<TRef[] | null>(null);
  const [expanding, setExpanding] = useState(false);

  const loadAllItems = async () => {
    if (allItemsRef.current) return allItemsRef.current;
    const cache = getLabelCache(cacheKey);
    const items: TRef[] = [];
    let next: string | undefined;
    do {
      const page = await fetchPage({ search: '', cursor: next, limit: ALL_PAGE_LIMIT });
      for (const raw of page.data) {
        const option = toOption(raw);
        cache.set(option.value, option.label);
        items.push(toItem(raw));
      }
      next = page.hasNextPage ? page.nextCursor : undefined;
    } while (next);
    allItemsRef.current = items;
    return items;
  };

  const handleChange = async (raw: unknown) => {
    if (expanding) return;
    const values = raw as string[];
    const picked = values.filter((v) => v !== ALL_VALUE);
    if (value.all) {
      // Every row is drawn checked under "All", but antd's own value is only `[ALL_VALUE]`, so a
      // click on a row reaches here as a *pick* of it. It means "uncheck this one": expand to every
      // item except it. Unchecking the "All" row itself (or its tag ✕) leaves no values → empty.
      if (picked.length === 0) {
        onChange({ all: false, ids: [] });
        return;
      }
      const unchecked = new Set(picked);
      setExpanding(true);
      try {
        const all = await loadAllItems();
        onChange({ all: false, ids: all.filter((r) => !unchecked.has(itemId(r))) });
      } catch {
        // Could not load the full list — leave "All" as it was rather than guess.
      } finally {
        setExpanding(false);
      }
      return;
    }
    // Adding "All" while it wasn't already active → collapse to the flag.
    if (values.includes(ALL_VALUE)) {
      onChange({ all: true, ids: [] });
      return;
    }
    // Any individual pick rebuilds the ref list.
    const ids = picked.map((v) => refByValue.get(v)).filter((r): r is TRef => r != null);
    // Re-picking the last missing item collapses back to "All". Only answerable once the full list
    // is known (it is after an uncheck-from-All), so an unknown list never triggers a fetch here.
    const all = allItemsRef.current;
    if (all && all.length > 0 && ids.length > value.ids.length) {
      const pickedSet = new Set(ids.map(itemId));
      if (all.every((r) => pickedSet.has(itemId(r)))) {
        onChange({ all: true, ids: [] });
        return;
      }
    }
    onChange({ all: false, ids });
  };

  return (
    <Select
      {...rest}
      mode="multiple"
      style={{ width: '100%' }}
      value={selected}
      options={options}
      // Real options are server-searched (via `useCursorSelect`), so they always pass; the
      // client-injected "All" row is the only thing filtered here — otherwise it would keep the
      // list non-empty and suppress the "No Results Found" state on a no-match search. The
      // effective `filterOption` lives inside `showSearch` in antd 6 (top-level is deprecated).
      showSearch={{
        ...cursor.showSearch,
        filterOption: (input, option) =>
          option?.value !== ALL_VALUE ||
          String(option?.label ?? '')
            .toLowerCase()
            .includes(input.trim().toLowerCase()),
      }}
      loading={cursor.loading || expanding}
      onPopupScroll={cursor.onPopupScroll}
      // antd clears its own search input on close, but `useCursorSelect`'s term would persist —
      // reset it so reopening after a no-match search isn't still filtered to zero real options.
      onOpenChange={(open) => {
        if (!open) cursor.resetSearch();
      }}
      onChange={(v) => void handleChange(v)}
      // Left square checkboxes (Figma 4612-1048818) — drop antd's default right checkmark and
      // render a presentational checkbox on every row; the row click toggles selection via antd.
      menuItemSelectedIcon={null}
      optionRender={(option) => (
        <span className={styles.optionRow}>
          <Checkbox
            // "All" checks every individual row too (AC 109605); the "All" row's own check still
            // comes from `selectedSet` since `selected` is `[ALL_VALUE]` when `value.all` (114800/
            // 114802/114804).
            checked={value.all || selectedSet.has(String(option.value))}
            tabIndex={-1}
            aria-hidden
            onChange={() => {}}
          />
          {option.label}
        </span>
      )}
      // No-results state (Figma 8578-809622): muted magnifier + "No Results Found".
      notFoundContent={
        cursor.loading ? undefined : (
          <div className={styles.empty}>
            <MagnifyingGlassIcon size={28} aria-hidden />
            <span>No Results Found</span>
          </div>
        )
      }
    />
  );
}
